import React, { useEffect, useState, useRef, useLayoutEffect } from 'react';

const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:5001';

// Build a tree structure: { email, name, children: [] }
function buildTree(employees) {
  const nodes = {};
  
  // Create nodes
  Object.entries(employees).forEach(([email, data]) => {
    nodes[email] = {
      email,
      name: `${data.first_name} ${data.last_name}`.trim() || email,
      title: data.title || '',
      country: data.country || '',
      start_date: data.start_date || '',
      referral_count: data.referral_count || 0,
      children: []
    };
  });

  const roots = [];
  
  // Build parent-child relationships based purely on manager_email
  Object.entries(employees).forEach(([email, data]) => {
    const mgr = (data.manager_email || '').toLowerCase();
    if (mgr && mgr !== email && nodes[mgr]) {
      nodes[mgr].children.push(nodes[email]);
    } else {
      // no manager or self-managed → root
      roots.push(nodes[email]);
    }
  });

  // Simple alphabetical sort for consistent rendering
  const sortAlphabetically = (nodeArray) => {
    return nodeArray.sort((a, b) => {
      return (a.name || a.email).localeCompare(b.name || b.email);
    });
  };

  // Recursively sort all levels alphabetically
  const sortTreeRecursively = (nodeArray) => {
    const sorted = sortAlphabetically(nodeArray);
    sorted.forEach(node => {
      if (node.children && node.children.length > 0) {
        node.children = sortTreeRecursively(node.children);
      }
    });
    return sorted;
  };

  return sortTreeRecursively(roots);
}

const NodeCard = ({ node, currentUserEmail, className }) => {
  const isCurrentUser = node.email.toLowerCase() === (currentUserEmail || '').toLowerCase();
  const isFutureStart = node.start_date && new Date(node.start_date) > new Date();
  const futureTooltipText = isFutureStart ? `This person starts on ${node.start_date}` : '';

  return (
    <div
      className={`org-node ${isCurrentUser ? 'current-user' : ''} ${isFutureStart ? 'future-start' : ''} ${className || ''}`}
    >
      {isFutureStart && (
        <>
          <span className="org-future-emoji" role="img" aria-label="Future employee">🆕</span>
          <span className="org-future-tooltip">{futureTooltipText}</span>
        </>
      )}
      {node.referral_count > 0 && (
        <>
          <div className="org-referral-badge"></div>
          <span className="org-referral-tooltip">Thanks to {node.name} for referring {node.referral_count} employee{node.referral_count === 1 ? '' : 's'}!</span>
        </>
      )}
      <div className="org-name">{node.name || node.email}</div>
      {node.title && <div className="org-title">{node.title}</div>}
      {node.country && <div className="org-country">{node.country}</div>}
    </div>
  );
};

const Node = ({ node, currentUserEmail }) => {
  const branchChildren = (node.children || []).filter(c => c.children && c.children.length > 0);
  const leafChildren = (node.children || []).filter(c => !c.children || c.children.length === 0);
  const hasLeafGroup = leafChildren.length >= 2;
  const ungroupedLeaves = hasLeafGroup ? [] : leafChildren;
  const groupedLeaves = hasLeafGroup ? leafChildren : [];
  const treeChildren = [...branchChildren, ...ungroupedLeaves];

  return (
    <li className="org-item">
      <NodeCard node={node} currentUserEmail={currentUserEmail} />
      {(treeChildren.length > 0 || groupedLeaves.length > 0) && (
        <ul>
          {treeChildren.map(child => (
            <Node key={child.email} node={child} currentUserEmail={currentUserEmail} />
          ))}
          {groupedLeaves.length > 0 && (
            <li className="org-item org-leaf-group-item">
              <div className="org-leaf-group">
                {groupedLeaves.map(leaf => (
                  <NodeCard key={leaf.email} node={leaf} currentUserEmail={currentUserEmail} className="org-node-compact" />
                ))}
              </div>
            </li>
          )}
        </ul>
      )}
    </li>
  );
};

export default function OrgChartPage({ user }) {
  const [employees, setEmployees] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Zoom level (0.1x – 2x)
  const [zoom, setZoom] = useState(1);
  const chartRef = useRef(null);
  const scrollRef = useRef(null);
  const wrapperRef = useRef(null);

  // Drag panning state
  const [isDragging, setIsDragging] = useState(false);
  const [dragStart, setDragStart] = useState({ x: 0, y: 0 });
  const [scrollStart, setScrollStart] = useState({ left: 0, top: 0 });

  // Auto-fit chart to viewport (both width and height)
  const fitChartToViewport = () => {
    if (!chartRef.current || !wrapperRef.current) return;
    
    const chartWidth = chartRef.current.offsetWidth;
    const chartHeight = chartRef.current.offsetHeight;
    if (!chartWidth || !chartHeight) return;
    
    const wrapperWidth = wrapperRef.current.offsetWidth;
    const wrapperHeight = wrapperRef.current.offsetHeight;
    
    // Calculate zoom to fit both width and height
    const widthRatio = wrapperWidth / chartWidth;
    const heightRatio = wrapperHeight / chartHeight;
    
    // Use the smaller ratio to ensure the entire chart fits
    const ratio = Math.min(widthRatio, heightRatio);
    
    // Apply some padding (90% of available space)
    const computed = Math.min(2, Math.max(0.1, Math.round(ratio * 0.9 * 10) / 10));
    setZoom(computed);
    
    // Center the chart after zoom is applied
    setTimeout(() => {
      if (!scrollRef.current || !chartRef.current) return;
      
      const scaledWidth = chartWidth * computed;
      const scaledHeight = chartHeight * computed;
      
      // Calculate scroll positions to center the chart
      const scrollLeft = Math.max(0, (scaledWidth - wrapperWidth) / 2);
      const scrollTop = Math.max(0, (scaledHeight - wrapperHeight) / 2);
      
      scrollRef.current.scrollLeft = scrollLeft;
      scrollRef.current.scrollTop = scrollTop;
    }, 0);
  };

  useLayoutEffect(() => {
    // run after roots change (data loaded)
    fitChartToViewport();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [employees]);

  useEffect(() => {
    const handleResize = () => fitChartToViewport();
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Drag panning handlers
  const handleMouseDown = (e) => {
    if (!scrollRef.current) return;
    setIsDragging(true);
    setDragStart({ x: e.clientX, y: e.clientY });
    setScrollStart({ 
      left: scrollRef.current.scrollLeft, 
      top: scrollRef.current.scrollTop 
    });
    e.preventDefault();
  };

  const handleMouseMove = (e) => {
    if (!isDragging || !scrollRef.current) return;
    const dx = e.clientX - dragStart.x;
    const dy = e.clientY - dragStart.y;
    scrollRef.current.scrollLeft = scrollStart.left - dx;
    scrollRef.current.scrollTop = scrollStart.top - dy;
  };

  const handleMouseUp = () => {
    setIsDragging(false);
  };

  // Add global mouse event listeners for drag
  useEffect(() => {
    if (isDragging) {
      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);
      return () => {
        document.removeEventListener('mousemove', handleMouseMove);
        document.removeEventListener('mouseup', handleMouseUp);
      };
    }
  }, [isDragging, dragStart, scrollStart]);

  const zoomOut = () => setZoom(z => Math.max(0.1, Math.round((z - 0.1) * 10) / 10));
  const zoomIn = () => setZoom(z => Math.min(2, Math.round((z + 0.1) * 10) / 10));
  const resetZoom = () => fitChartToViewport();

  // Handle mousewheel zoom
  const handleWheel = (e) => {
    if (e.ctrlKey || e.metaKey) {
      e.preventDefault();
      const delta = e.deltaY > 0 ? -0.1 : 0.1;
      setZoom(z => Math.min(2, Math.max(0.1, Math.round((z + delta) * 10) / 10)));
    }
  };

  useEffect(() => {
    const fetchEmployees = async () => {
      try {
        const res = await fetch(`${API_BASE}/api/org_chart/employees`, { credentials: 'include' });
        if (!res.ok) throw new Error(`Failed ${res.status}`);
        const data = await res.json();
        setEmployees(data.employees || {});
      } catch (e) {
        setError(e.message);
      } finally {
        setLoading(false);
      }
    };
    fetchEmployees();
  }, []);

  if (loading) return <div>Loading org chart…</div>;
  if (error) return <div style={{ color:'red' }}>{error}</div>;

  const roots = buildTree(employees);
  const currentUserEmail = user?.email;

  return (
    <div className="org-chart-container" style={{ height: 'calc(100vh - 200px)', display: 'flex', flexDirection: 'column' }}>
      {/* Header with Zoom controls */}
      <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:16 }}>
        <h2 className="content-title" style={{ margin:0 }}>Organization Chart</h2>
        <div style={{ display:'flex', alignItems:'center', gap:'8px' }}>
          <button className="secondary-button" onClick={zoomOut} style={{ padding:'4px 8px' }}>-</button>
          <span style={{ minWidth:40, textAlign:'center' }}>{Math.round(zoom*100)}%</span>
          <button className="secondary-button" onClick={zoomIn} style={{ padding:'4px 8px' }}>+</button>
          <button className="secondary-button" onClick={resetZoom} style={{ padding:'4px 12px', marginLeft:'8px' }}>Fit</button>
        </div>
      </div>

      {/* Scrollable & Zoomable chart */}
      <div 
        ref={wrapperRef}
        style={{ flex: 1, overflow: 'hidden', position: 'relative', border: '1px solid #e5e7eb', borderRadius: '8px', background: '#fafafa' }}
      >
        <div 
          ref={scrollRef} 
          className="org-chart-wrapper"
          onMouseDown={handleMouseDown}
          onWheel={handleWheel}
          style={{ 
            cursor: isDragging ? 'grabbing' : 'grab',
            width: '100%',
            height: '100%',
            overflow: 'auto',
            position: 'relative'
          }}
        >
          <div 
            ref={chartRef} 
            style={{ 
              transform:`scale(${zoom})`, 
              transformOrigin:'top left',
              display:'inline-block',
              padding: '40px',
              minWidth: 'max-content'
            }}
          >
            <ul className="org-chart">
              {roots.map(r => (
                <Node key={r.email} node={r} currentUserEmail={currentUserEmail} />
              ))}
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
} 